import { memo, useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';

import {
  Button,
  Row,
  Avatar,
  Space,
  Typography,
  Divider,
  Badge,
  List,
  Flex,
  Spin,
  Popconfirm,
  Modal,
  Dropdown,
  Card,
  Tag,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  AuditOutlined,
  LockOutlined,
  InfoCircleFilled,
  ExclamationCircleFilled,
} from '@ant-design/icons';

import { usePermissions } from '../../../../context/permission.context';

import Layout from '../../../../components/layout';
import Breadcrumb from '../../../../components/common/breadcrumb';
import CaptionInfo from '../../../../components/common/caption-info';

import styles from './index.module.css';
import {
  Candidate as CandidateType,
  HiringManager,
  CandidateFeedback,
  CandidateInterview,
  CandidateOfferEvent,
  CandidateOfferEventType,
  Status,
  SUPPORTED_SCORING_VERSION,
} from '../../../../fsm/jobs';
import AddFeedbackModal from '../../components/candidate/add-feedback-modal';
import ViewFeedbackModal from '../../components/candidate/view-feedback-modal';
import DebriefModal from '../../components/candidate/debrief-modal';
import type { DebriefPayload } from '../../components/candidate/debrief-modal';
import ShareProfileModal from '../../components/candidate/share-profile-modal';
import InterviewTranscriptModal from '../../components/candidate/interview-transcript-modal';
import OfferEventsPanel from '../../components/candidate/offer-events-panel';
import type { RecordOfferEventPayload } from '../../components/candidate/record-offer-event-modal';
import { roleLabelMapping, UserRole } from '../../../users/utils/role';
import { apiInstantToDate } from '../../../../utils/apiInstant';

import { User } from '../../../../fsm/users';
import {
  isScoredDimension,
  humanizeGeneratedText,
} from '../../utils/dimensions';
import { displayCandidateName } from '../../utils/candidateDisplay';
import MaskedValue from '../../components/candidate/masked-value';
import MaskedResumeModal from '../../components/candidate/masked-resume-modal';
import DimensionRow from '../../components/candidate/dimension-row';
import PipelineTrack from '../../components/candidate/pipeline-track';
import ScoreBlock from '../../components/candidate/score-block';
import InterviewStatusBadge from '../../components/candidate/interview-status-badge';
import InterviewProgressBanner from '../../components/candidate/interview-progress-banner';
import InterviewGuide, {
  hasInterviewGuideContent,
} from '../../components/candidate/interview-guide';
import StandoutQuote, {
  hasStandoutQuote,
} from '../../components/candidate/standout-quote';
import AiFluency, {
  hasAiFluency,
} from '../../components/candidate/ai-fluency';
import DimensionScoreHistory from '../../components/candidate/dimension-score-history';
import WorkReplaySection from '../../components/candidate/work-replay';
import { isWorkReplayEnabled } from '../../work-replay/flag';
import FitScoreHistoryNote from '../../components/candidate/fit-score-history-note';
import TrainingWheelsHint, {
  markHintDone,
} from '../../../../components/common/training-wheels-hint';
import type { CandidateInterviewStatus } from '../../hooks/useCandidateInterview';

const { Text } = Typography;

const TRANSCRIPT_HINT_KEY = 'candidate-interview-transcript';
const TRANSCRIPT_HINT_MESSAGE =
  'Your interview transcripts will appear here once the candidate completes their Rio interview.';

const grayAI = `${process.env.PUBLIC_URL}/images/gray-ai.svg`;
export const MASKED_INVITE_NOTE =
  'Inviting is not available in the anonymised view: it would e-mail the real person.';
export const MASKED_SHARE_NOTE =
  'Sharing is not available in the anonymised view: it would send the real profile to a colleague.';

interface Props {
  user?: User;
  isLoading: boolean;
  candidate?: CandidateType;
  /** Id of the candidate whose load failed, if the last one did. */
  candidateLoadFailedFor?: number;
  candidateFeedback: CandidateFeedback | null;
  candidateInterview: CandidateInterview | null;
  candidateInterviewStatus: CandidateInterviewStatus;
  hiringManagers: HiringManager[];
  offerEvents: CandidateOfferEvent[];
  isRecordingOfferEvent: boolean;
  onGetFeedback: (jobId: number, candidateId: number) => void;
  onGetCandidateInterview: (jobId: number, candidateId: number) => void;
  onGetOfferEvents: (jobId: number, candidateId: number) => void;
  onRecordOfferEvent: (
    jobId: number,
    candidateId: number,
    eventType: CandidateOfferEventType,
    payload: RecordOfferEventPayload,
  ) => void;
  onAddFeedback: (
    jobId: number,
    candidateId: number,
    text: string,
    rating?: number,
    reason?: string,
  ) => void;
  onGetCandidate: (jobId: number, candidateId: number) => void;
  onFetchHiringManagers: (jobId: number) => void;
  onDownloadCandidateCV: (jobId: number, candidateId: number) => void;
  onShareCandidate: (
    jobId: number,
    candidateId: number,
    userId: number,
  ) => void;
  onUpdateCandidateStatus: (
    jobId: number,
    candidateId: number,
    status: string,
  ) => void;
}

const Candidate = ({
  user,
  isLoading,
  candidate: loadedCandidate,
  candidateLoadFailedFor,
  candidateFeedback,
  candidateInterview,
  candidateInterviewStatus,
  hiringManagers,
  offerEvents,
  isRecordingOfferEvent,
  onGetCandidate,
  onAddFeedback,
  onGetFeedback,
  onGetCandidateInterview,
  onGetOfferEvents,
  onRecordOfferEvent,
  onFetchHiringManagers,
  onUpdateCandidateStatus,
  onDownloadCandidateCV,
  onShareCandidate,
}: Props) => {
  const [isAddFeedbackModalOpen, setIsAddFeedbackModalOpen] =
    useState<boolean>(false);
  const [isViewFeedbackModalOpen, setIsViewFeedbackModalOpen] =
    useState<boolean>(false);
  const [isMaskedResumeOpen, setIsMaskedResumeOpen] = useState<boolean>(false);
  const [isShareProfileModalOpen, setIsShareProfileModalOpen] =
    useState<boolean>(false);
  const [isInterviewTranscriptModalOpen, setIsInterviewTranscriptModalOpen] =
    useState<boolean>(false);
  // Which decision the debrief is being asked about. Null means closed.
  // RIO-7.3 asks at the moment of the stage change, so this is set by the
  // decision handlers rather than by a button.
  const [debriefFor, setDebriefFor] = useState<Status | null>(null);
  const { jobId, candidateId } = useParams();
  const parsedCandidateId = candidateId ? parseInt(candidateId, 10) : NaN;
  const isValidCandidateId = Number.isInteger(parsedCandidateId);
  // On a route change the machine still holds the previous candidate until
  // the new request starts; never act on or show a profile that is not the
  // one in the URL (the status effect below would mark the wrong candidate).
  const candidate =
    loadedCandidate && loadedCandidate.id === parsedCandidateId
      ? loadedCandidate
      : undefined;
  const { confirm } = Modal;
  const {
    canSeeCandidateRejectButton,
    canSeeCandidateViewHMFeedbackButton,
    canSeeCandidateAddFeedbackButton,
    canSeeCandidateNextStageButton,
    canSeeOfferTrackingButtons,
  } = usePermissions();
  // Stopgap, not a new Permission: recording an offer event now triggers
  // billing (CandidateOfferEventService#recordOfferEvent), and Nachi ruled
  // 2026-10-01 that HIRING_MANAGER loses that specifically, while
  // CAN_SEE_OFFER_TRACKING_BUTTONS still grants it to HIRING_MANAGER (it also
  // gates the read-only history view, which HIRING_MANAGER keeps). Allow-list
  // rather than `role !== HIRING_MANAGER`, so a role added to UserRole later
  // defaults to not-allowed here until this is deliberately extended - the
  // real fix is a dedicated CAN_RECORD_OFFER_EVENT capability, tracked
  // alongside the customer-config role work.
  const canRecordOfferEvents =
    canSeeOfferTrackingButtons &&
    !!user?.role &&
    [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.RECRUITER].includes(
      user.role,
    );

  useEffect(() => {
    if (jobId && isValidCandidateId) {
      onGetCandidate(parseInt(jobId), parsedCandidateId);
    }
  }, [jobId, isValidCandidateId, parsedCandidateId]);

  useEffect(() => {
    if (jobId && isValidCandidateId) {
      onGetCandidateInterview(parseInt(jobId), parsedCandidateId);
    }
  }, [jobId, isValidCandidateId, parsedCandidateId]);

  useEffect(() => {
    if (jobId && isValidCandidateId) {
      onGetOfferEvents(parseInt(jobId), parsedCandidateId);
    }
  }, [jobId, isValidCandidateId, parsedCandidateId]);

  const handleRecordOfferEvent = useCallback(
    (eventType: CandidateOfferEventType, payload: RecordOfferEventPayload) => {
      if (jobId && isValidCandidateId) {
        onRecordOfferEvent(
          parseInt(jobId),
          parsedCandidateId,
          eventType,
          payload,
        );
      }
    },
    [jobId, isValidCandidateId, parsedCandidateId, onRecordOfferEvent],
  );

  useEffect(() => {
    if (candidate?.status === Status.NEW && jobId && isValidCandidateId) {
      onUpdateCandidateStatus(
        parseInt(jobId),
        parsedCandidateId,
        Status.REVIEWED,
      );
    }
  }, [
    candidate?.status,
    jobId,
    isValidCandidateId,
    parsedCandidateId,
    onUpdateCandidateStatus,
  ]);

  const confirmReject = useCallback(() => {
    if (jobId && isValidCandidateId) {
      // The decision is recorded first and the debrief asked afterwards. Doing
      // it the other way round makes the debrief a gate on the decision, which
      // RIO-7.10 forbids, and risks losing the decision if the modal is closed.
      onUpdateCandidateStatus(
        parseInt(jobId),
        parsedCandidateId,
        Status.REJECTED,
      );
      if (canSeeCandidateAddFeedbackButton) {
        setDebriefFor(Status.REJECTED);
      }
    }
  }, [
    jobId,
    isValidCandidateId,
    parsedCandidateId,
    onUpdateCandidateStatus,
    canSeeCandidateAddFeedbackButton,
  ]);

  const confirmNextStage = useCallback(() => {
    if (jobId && isValidCandidateId) {
      let status;
      if (candidate?.status === Status.HM_REVIEW) {
        status = Status.HIRED;
      } else if (candidate?.status === Status.REVIEWED) {
        status = Status.INVITATION_SENT;
      }
      // The anonymised view (Candidate.masked): an invitation e-mails the real person,
      // and the server refuses it for this viewer (403). Say so here, before the call.
      if (status === Status.INVITATION_SENT && candidate?.masked) {
        message.info(MASKED_INVITE_NOTE);
        return;
      }
      status &&
        onUpdateCandidateStatus(parseInt(jobId), parsedCandidateId, status);
      // Only HIRED is a hiring decision. Advancing REVIEWED to INVITATION_SENT
      // is workflow, and asking for a debrief on it would train people to
      // dismiss the box before reading it.
      if (status === Status.HIRED && canSeeCandidateAddFeedbackButton) {
        setDebriefFor(Status.HIRED);
      }
    }
  }, [
    jobId,
    isValidCandidateId,
    parsedCandidateId,
    candidate?.status,
    candidate?.masked,
    onUpdateCandidateStatus,
    canSeeCandidateAddFeedbackButton,
  ]);

  const handleDownloadCandidateCV = useCallback(() => {
    // The anonymised view: the server refuses the file for this viewer (403), so
    // nothing is fetched; the stand-in says what the button would have opened.
    if (candidate?.masked) {
      setIsMaskedResumeOpen(true);
      return;
    }
    // Called straight from the click: the hook opens the tab now, while the
    // browser still counts it as this click, and fills it when the link comes.
    if (jobId && isValidCandidateId) {
      onDownloadCandidateCV(parseInt(jobId), parsedCandidateId);
    }
  }, [
    jobId,
    isValidCandidateId,
    parsedCandidateId,
    candidate?.masked,
    onDownloadCandidateCV,
  ]);

  const handleAddFeedbackModalOpen = useCallback(() => {
    setIsAddFeedbackModalOpen(true);
  }, [setIsAddFeedbackModalOpen]);

  const handleAddFeedbackModalClose = useCallback(() => {
    setIsAddFeedbackModalOpen(false);
  }, [setIsAddFeedbackModalOpen]);

  const handleDebriefClose = useCallback(() => {
    setDebriefFor(null);
  }, []);

  const handleDebriefSubmit = useCallback(
    ({ rating, reason, text }: DebriefPayload) => {
      if (!jobId || !isValidCandidateId) return;
      onAddFeedback(
        parseInt(jobId),
        parsedCandidateId,
        text,
        rating || undefined,
        reason || undefined,
      );
    },
    [jobId, isValidCandidateId, parsedCandidateId, onAddFeedback],
  );

  const handleViewFeedbackModalOpen = useCallback(() => {
    setIsViewFeedbackModalOpen(true);
  }, [setIsViewFeedbackModalOpen]);

  const handleViewFeedbackModalClose = useCallback(() => {
    setIsViewFeedbackModalOpen(false);
  }, [setIsViewFeedbackModalOpen]);

  const handleShareProfileModalOpen = useCallback(() => {
    // Sharing puts the real person in a colleague's inbox; refused for a masked viewer.
    if (candidate?.masked) {
      message.info(MASKED_SHARE_NOTE);
      return;
    }
    setIsShareProfileModalOpen(true);
  }, [candidate?.masked, setIsShareProfileModalOpen]);

  const handleShareProfileModalClose = useCallback(() => {
    setIsShareProfileModalOpen(false);
  }, [setIsShareProfileModalOpen]);

  const handleInterviewTranscriptModalOpen = useCallback(() => {
    // They have found the transcript, so the hint that pointed at it is done.
    markHintDone(TRANSCRIPT_HINT_KEY);
    setIsInterviewTranscriptModalOpen(true);
  }, [setIsInterviewTranscriptModalOpen]);

  const handleInterviewTranscriptModalClose = useCallback(() => {
    setIsInterviewTranscriptModalOpen(false);
  }, [setIsInterviewTranscriptModalOpen]);

  const displayName = useMemo(
    () => displayCandidateName(candidate?.name),
    [candidate?.name],
  );

  const explanatoryNote = useMemo(() => {
    if (!candidate?.explanatoryNote) return null;
    return humanizeGeneratedText(
      candidate.explanatoryNote,
      Object.keys(candidate.dimensions ?? {}),
    );
  }, [candidate?.explanatoryNote, candidate?.dimensions]);

  // 'unknown' is the backend's forward-compatibility escape hatch on its own
  // read path, not a real interview state -- treated the same as
  // 'not_started' here, per avrio-backend's own Status enum comment.
  const isInterviewInProgress = candidateInterview?.status === 'in_progress';

  // Same test the transcript modal uses for "there is something to show".
  const hasTranscript = (
    candidateInterview?.transcript?.conversations ?? []
  ).some((conversation) => conversation.messages.length > 0);

  const isInterviewLive = useMemo(() => {
    // Epoch seconds from the API, not ISO text: Date.parse gave NaN, so a live interview never read as live.
    const updated = apiInstantToDate(candidateInterview?.updatedAt);
    if (!updated) return false;
    return Date.now() - updated.getTime() < 5 * 60 * 1000;
  }, [candidateInterview?.updatedAt]);

  // LOAD-BEARING: the interview's numbers (its score and its per-dimension
  // observations) are shown as comparable with the resume-derived ones only
  // when Rio's scoring version stamp says they came from the scorer this page
  // reads (SUPPORTED_SCORING_VERSION). Rio writes the stamp when the
  // interview completes, so a running interview has none yet and its live
  // observations are trusted as the current scorer's; a completed record
  // with a different stamp, or none at all (written before Rio stamped
  // versions, 2026-09-05), falls back to the resume score exactly as if no
  // comparable interview score existed. Until 2026-10-20 this trusted null
  // and refused every stamped value, which hid the interview's dimension
  // scores on every completed interview since Rio began stamping them.
  const scoringVersion = candidateInterview?.scoringVersion ?? null;
  const canTrustInterviewScoreScale =
    scoringVersion === SUPPORTED_SCORING_VERSION ||
    (scoringVersion === null && candidateInterview?.completed === false);
  // Rio's rescore, written onto the candidate's own DeepMatchingResults item
  // when an interview completes and mapped to candidate.rescore by
  // avrio-backend. A candidacy is ONE Fit Score record that the interview
  // updates (product ruling, 2026-09-05, reconfirmed 2026-10-05), so a rescore
  // wins over everything else shown here: scoreAfter is the Fit Score and
  // scoreBefore is the "was". Both come from Rio's own scale, deliberately
  // not mixed with the matcher's dm_score (candidate.fitScore), which is a
  // separate derivation and can differ from scoreBefore. Absent means the
  // candidate has not completed an interview (the API contract's own wording),
  // so a rescore is also proof of a completed interview, independent of
  // whether InterviewResults holds a row under this candidate's id.
  const rescore = candidate?.rescore;
  const hasRescore =
    typeof rescore?.scoreBefore === 'number' &&
    Number.isFinite(rescore.scoreBefore) &&
    typeof rescore?.scoreAfter === 'number' &&
    Number.isFinite(rescore.scoreAfter);
  const interviewCompleted =
    Boolean(candidateInterview?.completed) || hasRescore;
  const displayedFitScore = hasRescore
    ? rescore.scoreAfter
    : canTrustInterviewScoreScale &&
        typeof candidateInterview?.interviewScore === 'number'
      ? candidateInterview.interviewScore
      : candidate?.fitScore;

  const totalScoredDimensions = useMemo(() => {
    if (!candidate?.dimensions) return 0;
    return Object.keys(candidate.dimensions).filter(isScoredDimension).length;
  }, [candidate?.dimensions]);

  const disableNextStageButton = useMemo(() => {
    return (
      candidate?.status === Status.ALT_INTERVIEW ||
      candidate?.status === Status.RIO_SCREENING ||
      candidate?.status === Status.INVITATION_SENT ||
      candidate?.status === Status.REJECTED ||
      candidate?.status === Status.NOT_INTERESTED ||
      candidate?.status === Status.HIRED
    );
  }, [candidate?.status]);

  const disableViewFeedbackButton = useMemo(() => {
    return !(
      candidate?.status === Status.HM_REVIEW ||
      candidate?.status === Status.REJECTED ||
      candidate?.status === Status.NOT_INTERESTED ||
      candidate?.status === Status.HIRED
    );
  }, [candidate?.status]);

  const nextBtnlabel = useMemo(() => {
    if (candidate?.status === Status.HM_REVIEW) {
      return 'Hire';
    } else if (candidate?.status === Status.REVIEWED) {
      return 'Send email';
    } else {
      return 'Advance next stage';
    }
  }, [candidate?.status]);

  const nextStagePopconfirmData = useMemo(() => {
    if (candidate?.status === Status.HM_REVIEW) {
      return {
        title: 'Hire candidate',
        description: 'Are you sure you want to Hire the candidate?',
      };
    } else if (candidate?.status === Status.REVIEWED) {
      return {
        title: 'Send email',
        description: 'Are you sure you want to send email to the candidate?',
      };
    } else {
      return {
        title: '',
        description: '',
      };
    }
  }, [candidate?.status]);

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const onMenuClick = (e: any) => {
    confirm({
      title: 'Confirmation',
      icon: <ExclamationCircleFilled />,
      content: (
        <Typography>
          {e.key === Status.HIRED &&
            'Are you sure you want to hire this candidate?'}
          {e.key === Status.NOT_INTERESTED &&
            'Are you sure that this candidate is not interested?'}
        </Typography>
      ),
      okText: 'Yes',
      onOk() {
        if (jobId && isValidCandidateId) {
          onUpdateCandidateStatus(parseInt(jobId), parsedCandidateId, e.key);
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  return (
    <Layout user={user}>
      <Breadcrumb
        renderLeft={
          <Button type="text" icon={<ArrowLeftOutlined />}>
            <Link to={`/jobs/${jobId}/candidates`}>Back to candidate list</Link>
          </Button>
        }
      />
      {!isValidCandidateId ? (
        <Flex align="center" justify="center" style={{ height: 400 }}>
          <Text type="secondary">This candidate link is invalid.</Text>
        </Flex>
      ) : isLoading ? (
        <Flex align="center" justify="center" style={{ height: 400 }}>
          <Spin tip="Loading..." />
        </Flex>
      ) : !candidate && candidateLoadFailedFor === parsedCandidateId ? (
        // Keyed on the id so a failure for another candidate (or an earlier
        // one) never flashes here on the way to this route.
        <Flex
          vertical
          align="center"
          justify="center"
          gap={16}
          style={{ height: 400 }}
        >
          <Text type="secondary">We couldn&apos;t load this candidate.</Text>
          <Button
            type="primary"
            onClick={() =>
              jobId && onGetCandidate(parseInt(jobId), parsedCandidateId)
            }
          >
            Try again
          </Button>
        </Flex>
      ) : (
        <>
          <div className={styles.infoWrapper}>
            <div className={styles.infoCard}>
              <Row
                align={'middle'}
                justify={'space-between'}
                style={{ width: '100%' }}
              >
                <Space size={24}>
                  <Avatar size={isMobile ? 40 : 100}>
                    {displayName
                      .split(' ')
                      .map((item) => item.charAt(0))
                      .join('')}
                  </Avatar>
                  <ScoreBlock
                    score={displayedFitScore}
                    badge={
                      isInterviewInProgress && (
                        <InterviewStatusBadge
                          variant={isInterviewLive ? 'live' : 'stalled'}
                        />
                      )
                    }
                    historyNote={
                      hasRescore ? (
                        <FitScoreHistoryNote
                          current={rescore.scoreAfter}
                          previous={rescore.scoreBefore}
                          label="before interview:"
                        />
                      ) : (
                        canTrustInterviewScoreScale &&
                        typeof candidateInterview?.interviewScore ===
                          'number' &&
                        typeof candidate?.fitScore === 'number' && (
                          <FitScoreHistoryNote
                            current={candidateInterview.interviewScore}
                            previous={candidate.fitScore}
                          />
                        )
                      )
                    }
                  />
                </Space>
                <Space>
                  <Button
                    type="text"
                    shape="round"
                    onClick={handleDownloadCandidateCV}
                  >
                    View resume
                  </Button>
                  <Button shape="round" onClick={handleShareProfileModalOpen}>
                    Share profile
                  </Button>
                </Space>
              </Row>
              {isInterviewInProgress && (
                <InterviewProgressBanner
                  variant={isInterviewLive ? 'live' : 'stalled'}
                  updatedCount={candidateInterview?.dimensionsAssessed ?? 0}
                  totalCount={totalScoredDimensions}
                />
              )}
              <Divider />
              <div className={styles.timelineWrapper}>
                <div>
                  <div className={styles.nameWrapper}>
                    <Text
                      strong={!isMobile}
                      style={{ fontSize: isMobile ? 14 : 20 }}
                    >
                      {displayName}
                    </Text>
                    {!isMobile && <Divider type="vertical" />}
                    <Text
                      strong={!isMobile}
                      style={{ fontSize: isMobile ? 14 : 20 }}
                    >
                      {candidate?.jobTitle}
                    </Text>
                  </div>
                  {candidate?.masked && (
                    <Row style={{ marginBottom: 8 }}>
                      <Tag
                        icon={<LockOutlined />}
                        color="geekblue"
                        title="This account sees candidates anonymised: the match is real, the identity is hidden."
                      >
                        Anonymised profile
                      </Tag>
                    </Row>
                  )}
                  <Row>
                    <Text type="secondary">
                      Email:{' '}
                      {candidate?.masked ? (
                        <MaskedValue kind="email" />
                      ) : (
                        candidate?.email
                      )}
                    </Text>
                  </Row>
                  <Row>
                    <Text type="secondary">
                      Phone:{' '}
                      {candidate?.masked ? (
                        <MaskedValue kind="phone" />
                      ) : (
                        candidate?.phoneNumber
                      )}
                    </Text>
                  </Row>
                </div>
                <div>
                  <Text strong style={{ fontSize: 20 }}>
                    Candidate status
                  </Text>
                  <div style={{ marginTop: 20 }}>
                    <PipelineTrack status={candidate?.status} />
                  </div>
                  {interviewCompleted && (
                    <div style={{ marginTop: 12 }}>
                      <Tag color="success">
                        Interview complete — ready to review
                      </Tag>
                      {hasTranscript ? (
                        <Button
                          type="link"
                          size="small"
                          onClick={handleInterviewTranscriptModalOpen}
                        >
                          View interview transcript
                        </Button>
                      ) : (
                        <Text type="secondary">Transcript unavailable</Text>
                      )}
                    </div>
                  )}
                  {/* Wait for the answer: "No interview yet" before the
                      interview has loaded would be a claim we cannot back. */}
                  {candidateInterviewStatus === 'loaded' &&
                    !interviewCompleted && (
                      <div style={{ marginTop: 12 }}>
                        <TrainingWheelsHint
                          storageKey={TRANSCRIPT_HINT_KEY}
                          message={TRANSCRIPT_HINT_MESSAGE}
                          anchor={
                            <Text type="secondary">
                              {isInterviewInProgress
                                ? 'Transcript available once the interview completes'
                                : 'No interview yet'}
                            </Text>
                          }
                        />
                      </div>
                    )}
                  {candidateInterviewStatus === 'error' && (
                    <div style={{ marginTop: 12 }}>
                      <Text type="secondary">
                        Interview data couldn&apos;t be loaded
                      </Text>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          if (jobId && isValidCandidateId) {
                            onGetCandidateInterview(
                              parseInt(jobId),
                              parsedCandidateId,
                            );
                          }
                        }}
                      >
                        Retry
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.dateWrapper}>
                <Space
                  size={20}
                  direction={isMobile ? 'vertical' : 'horizontal'}
                >
                  {candidate?.lastActivityAt && (
                    <Text>
                      Last active:{' '}
                      {new Intl.DateTimeFormat('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                      }).format(new Date(candidate.lastActivityAt * 1000))}
                    </Text>
                  )}
                  <Space>
                    <Badge status="success" />
                    <Text>
                      Updated: 
                      {candidate?.updatedAt &&
                        new Intl.DateTimeFormat('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric',
                        }).format(new Date(candidate.updatedAt * 1000))}
                    </Text>
                  </Space>
                </Space>
                <Space>
                  {canSeeCandidateRejectButton && (
                    <Popconfirm
                      title="Reject candidate"
                      description="Are you sure you want to reject the candidate?"
                      onConfirm={confirmReject}
                      icon={<InfoCircleFilled style={{ color: 'red' }} />}
                      okText="Reject"
                      cancelText="No"
                    >
                      <Button
                        type="text"
                        shape="round"
                        danger
                        size={isMobile ? 'small' : 'large'}
                      >
                        Reject
                      </Button>
                    </Popconfirm>
                  )}
                  {canSeeCandidateViewHMFeedbackButton && (
                    <Button
                      shape="round"
                      size={isMobile ? 'small' : 'large'}
                      disabled={disableViewFeedbackButton}
                      onClick={handleViewFeedbackModalOpen}
                    >
                      View HM feedback
                    </Button>
                  )}
                  {canSeeCandidateNextStageButton && (
                    <>
                      {/* Hire / Not interested is the human decision after HM review and after an
                          offer (C9: humans set HIRED). OFFERED used to fall through to a button with
                          no action behind it. */}
                      {candidate?.status === Status.HM_REVIEW ||
                      candidate?.status === Status.OFFERED ? (
                        <Dropdown.Button
                          type="primary"
                          disabled={disableNextStageButton}
                          size={isMobile ? 'small' : 'large'}
                          menu={{
                            items: [
                              {
                                key: Status.HIRED,
                                label: 'Hire',
                              },
                              {
                                key: Status.NOT_INTERESTED,
                                label: 'Not interested',
                              },
                            ],
                            onClick: onMenuClick,
                          }}
                        >
                          Next stage
                        </Dropdown.Button>
                      ) : (
                        <Popconfirm
                          title={nextStagePopconfirmData.title}
                          description={nextStagePopconfirmData.description}
                          onConfirm={confirmNextStage}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            type="primary"
                            shape="round"
                            size={isMobile ? 'small' : 'large'}
                            disabled={disableNextStageButton}
                          >
                            {nextBtnlabel}
                          </Button>
                        </Popconfirm>
                      )}
                    </>
                  )}
                  {canSeeCandidateAddFeedbackButton && (
                    <Button
                      type="primary"
                      shape="round"
                      size={isMobile ? 'small' : 'large'}
                      onClick={handleAddFeedbackModalOpen}
                    >
                      Add feedback
                    </Button>
                  )}
                </Space>
              </div>
            </div>
          </div>
          <div className={styles.wrapper}>
            <div className={styles.content} style={{ marginBottom: 24 }}>
              <Space style={{ marginBottom: 24 }}>
                <Text strong style={{ fontSize: 20 }}>
                  Dimensions
                </Text>
                {/*
                  UPDATED 2026-09-18: avrio-rio#87 and avrio-backend#105 join
                  the two pipelines this comment used to say were separate --
                  a dimension row below now shows the interview's own
                  observation once Rio has assessed it, not just the
                  resume-derived value. This comment previously said an
                  interview moving a dimension from 2 to 4 would still show
                  the 2; that is no longer true, and left stale would have
                  actively misled the next reader.

                  What's still true: avrio-backend serves the RESUME-DERIVED
                  baseline from DeepMatchingResults via
                  DeepMatchingResultsDynamoService / JobService, independently
                  of whatever the interview later assesses -- so a dimension
                  the interview hasn't reached yet still shows exactly that
                  baseline, correctly. `scoringVersion` (see
                  CandidateInterview's own type comment) is the one remaining
                  gate: null today, meaning the interview's 1-5 dimension
                  scores are safe to show and compare against the resume
                  baseline as-is.

                  Moved from a permanent caption line to this on-demand icon:
                  design review found 7+ headings across this page and the
                  dashboard each paying the vertical space and attention cost
                  of a full caveat sentence on every single view.
                */}
                <CaptionInfo
                  label="About dimensions"
                  text="Resume-derived by default. Once Rio assesses a dimension in the interview, that becomes the score shown here — click a changed dimension's history icon to see what it was before."
                />
                <img src={grayAI} alt="AI-generated" />
              </Space>
              <Flex gap="4px 0" wrap style={{ marginBottom: 48 }}>
                {candidate?.dimensions ? (
                  <Space
                    direction="vertical"
                    size={16}
                    style={{ width: '100%' }}
                  >
                    {(() => {
                      const entries = Object.entries(candidate.dimensions);
                      const scored = entries.filter(([k]) =>
                        isScoredDimension(k),
                      );
                      const unscored = entries.filter(
                        ([k]) => !isScoredDimension(k),
                      );

                      return (
                        <>
                          <Card size="small" style={{ width: '100%' }}>
                            {scored.map(([key, value]) => {
                              const observation =
                                canTrustInterviewScoreScale &&
                                candidateInterview?.observations?.[key];
                              const resumeScore = Number(value.score);
                              const hasInterviewChange =
                                observation &&
                                Number(observation.score) !== resumeScore;

                              return (
                                <DimensionRow
                                  key={key}
                                  dimensionKey={key}
                                  score={
                                    observation
                                      ? Number(observation.score)
                                      : resumeScore
                                  }
                                  justification={
                                    observation
                                      ? observation.justification
                                      : value.justification
                                  }
                                  historyBadge={
                                    hasInterviewChange && (
                                      <DimensionScoreHistory
                                        previousScore={resumeScore}
                                        previousJustification={
                                          value.justification
                                        }
                                      />
                                    )
                                  }
                                />
                              );
                            })}
                          </Card>

                          {unscored.length > 0 ? (
                            <div style={{ marginTop: 24, width: '100%' }}>
                              <Space style={{ marginBottom: 12 }}>
                                <Text strong>Recorded, not scored</Text>
                                <CaptionInfo
                                  label="About recorded, not scored"
                                  text="Doesn't affect the fit score — for context only."
                                />
                              </Space>
                              <Card size="small" style={{ width: '100%' }}>
                                {unscored.map(([key, value]) => (
                                  <DimensionRow
                                    key={key}
                                    dimensionKey={key}
                                    justification={value.justification}
                                  />
                                ))}
                              </Card>
                            </div>
                          ) : null}
                        </>
                      );
                    })()}
                  </Space>
                ) : (
                  '---'
                )}
              </Flex>
              <Space style={{ marginBottom: 24 }}>
                <Text strong style={{ fontSize: 20 }}>
                  Required skills
                </Text>
                <CaptionInfo
                  label="About required skills"
                  text="Found on the résumé — requirements they don't meet aren't listed."
                />
                <img src={grayAI} alt="AI-generated" />
              </Space>
              <div className={`${styles.skillRow} ${styles.sectionGap}`}>
                {candidate?.requiredSkills && candidate.requiredSkills.length
                  ? candidate.requiredSkills.map((item, index) => (
                      <span className={styles.skillMet} key={index}>
                        {item}
                      </span>
                    ))
                  : '---'}
              </div>
              <Space style={{ marginBottom: 24 }}>
                <Text strong style={{ fontSize: 20 }}>
                  Preferred skills
                </Text>
                <CaptionInfo
                  label="About preferred skills"
                  text="Found on the résumé — preferences they don't meet aren't listed."
                />
                <img src={grayAI} alt="AI-generated" />
              </Space>
              <div className={`${styles.skillRow} ${styles.sectionGap}`}>
                {candidate?.preferredSkills && candidate.preferredSkills.length
                  ? candidate.preferredSkills.map((item, index) => (
                      <span className={styles.skillPreferred} key={index}>
                        {item}
                      </span>
                    ))
                  : '---'}
              </div>
              <Space style={{ marginBottom: 24 }}>
                <Text strong style={{ fontSize: 20 }}>
                  Explanatory note
                </Text>
                <CaptionInfo
                  label="About the explanatory note"
                  text="Rio's reasoning behind the scores above, not a résumé summary."
                />
                <img src={grayAI} alt="AI-generated" />
              </Space>
              <div className={styles.sectionGap}>
                {explanatoryNote ? (
                  <div className={styles.notePanel}>{explanatoryNote}</div>
                ) : (
                  /* No panel when there is no rationale -- a tinted, rule-marked
                     empty box would suggest Rio had written something. */
                  <span className={styles.notePanelEmpty}>---</span>
                )}
              </div>
              {interviewCompleted &&
                hasStandoutQuote(candidateInterview?.standoutQuote) && (
                  <>
                    <Space style={{ marginBottom: 24 }}>
                      <Text strong style={{ fontSize: 20 }}>
                        In their words
                      </Text>
                      <CaptionInfo
                        label="About this quote"
                        text="One thing the candidate said in Rio's interview, chosen from an answer Rio scored as clear evidence. Their words, not Rio's."
                      />
                    </Space>
                    <div className={styles.sectionGap}>
                      <StandoutQuote quote={candidateInterview?.standoutQuote} />
                    </div>
                  </>
                )}
              {interviewCompleted &&
                hasAiFluency(candidateInterview?.aiFluency) && (
                  <>
                    <Space style={{ marginBottom: 24 }}>
                      <Text strong style={{ fontSize: 20 }}>
                        AI fluency
                      </Text>
                      <CaptionInfo
                        label="About AI fluency"
                        text="From Rio's three questions on how they use AI at work. Shown as evidence; not part of the fit score."
                      />
                      <img src={grayAI} alt="AI-generated" />
                    </Space>
                    <div className={styles.sectionGap}>
                      <AiFluency fluency={candidateInterview?.aiFluency} />
                    </div>
                  </>
                )}
              {isWorkReplayEnabled() &&
                interviewCompleted &&
                jobId &&
                isValidCandidateId && (
                  <>
                    <Space style={{ marginBottom: 24 }}>
                      <Text strong style={{ fontSize: 20 }}>
                        Work replay
                      </Text>
                      <CaptionInfo
                        label="About the work replay"
                        text="The candidate's work space from Rio's interview, replayed as they worked: the code as they typed it, their tests, the assistant, and each time they left the page. Evidence, not a score."
                      />
                    </Space>
                    <div className={styles.sectionGap}>
                      <WorkReplaySection
                        jobId={parseInt(jobId, 10)}
                        candidateId={parsedCandidateId}
                        masked={!!candidate?.masked}
                        onOpenTranscript={
                          hasTranscript
                            ? handleInterviewTranscriptModalOpen
                            : undefined
                        }
                      />
                    </div>
                  </>
                )}
              {interviewCompleted &&
                hasInterviewGuideContent(
                  candidateInterview?.interviewGuide,
                  candidateInterview?.candidateQuestions,
                ) && (
                  <>
                    <Space style={{ marginBottom: 24 }}>
                      <Text strong style={{ fontSize: 20 }}>
                        Interview guide
                      </Text>
                      <CaptionInfo
                        label="About the interview guide"
                        text="Questions for your interview, each mapped to what Rio's interview left open. Nothing generic."
                      />
                      <img src={grayAI} alt="AI-generated" />
                    </Space>
                    <div className={styles.sectionGap}>
                      <InterviewGuide
                        guide={candidateInterview?.interviewGuide}
                        questions={candidateInterview?.candidateQuestions}
                      />
                    </div>
                  </>
                )}
              <Space style={{ fontSize: 20, marginBottom: 24 }}>
                <AuditOutlined />
                <Text strong style={{ fontSize: 20 }}>
                  Candidate background
                </Text>
                <CaptionInfo
                  label="About candidate background"
                  text="The underlying record the assessment above was built from."
                />
              </Space>
              <Divider />
              <Space style={{ marginBottom: 24 }}>
                <Text strong style={{ fontSize: 20 }}>
                  Professional experience
                </Text>
                <CaptionInfo
                  label="About professional experience"
                  text="The full history as supplied — not a summary, not AI-written."
                />
              </Space>
              <List
                size="large"
                style={{ marginBottom: 48 }}
                header={<Text strong>Professional Experience</Text>}
                bordered
                dataSource={candidate?.experiences}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        item.employer ? (
                          <Text strong style={{ fontSize: 16 }}>
                            {item.employer}
                          </Text>
                        ) : candidate?.masked ? (
                          <MaskedValue kind="employer" />
                        ) : null
                      }
                      description={
                        <Space direction="vertical" size={0}>
                          <Text>{item.jobTitle}</Text>
                          {item.startDate && item.endDate && (
                            <Text type="secondary">
                              {new Intl.DateTimeFormat('en-US', {
                                year: 'numeric',
                                month: 'short',
                              }).format(new Date(item.startDate * 1000))}
                              {' - '}
                              {item.endDate
                                ? new Intl.DateTimeFormat('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                  }).format(new Date(item.endDate * 1000))
                                : 'present'}
                            </Text>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
              {/* No caption here, deliberately -- the previous one
                  ("Academic qualifications and degrees, providing insight
                  into their educational background") restated the heading
                  rather than explaining a method or caveat, which is the
                  smaller version of the exact problem CaptionInfo exists to
                  fix. Filler gets deleted, not hidden behind an icon. */}
              <Row style={{ marginBottom: 24 }}>
                <Text strong style={{ fontSize: 20 }}>
                  Education
                </Text>
              </Row>
              <List
                size="large"
                bordered
                style={{ marginBottom: 48 }}
                dataSource={candidate?.degrees}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        item.institution ? (
                          <Text strong style={{ fontSize: 16 }}>
                            {item.institution}
                          </Text>
                        ) : candidate?.masked ? (
                          <MaskedValue kind="institution" />
                        ) : null
                      }
                      description={
                        <Text>
                          {/* "Masters of " with nothing after it whenever
                              major is blank -- degree and major only join
                              with "of" when both are present. */}
                          {item.academicDegree && item.major
                            ? `${item.academicDegree} of ${item.major}`
                            : item.academicDegree || item.major || null}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
              <Row style={{ marginBottom: 24 }}>
                <Text strong style={{ fontSize: 20 }}>
                  Offer tracking
                </Text>
                <CaptionInfo
                  label="About offer tracking"
                  text="Recording an offer here is what triggers billing for this placement - see the confirmation step before it's recorded."
                />
              </Row>
              <div style={{ marginBottom: 48, width: '100%' }}>
                <OfferEventsPanel
                  isLoading={isRecordingOfferEvent}
                  offerEvents={offerEvents}
                  candidateName={displayName}
                  confirmingUserName={
                    user ? `${user.firstName} ${user.lastName}`.trim() : ''
                  }
                  confirmingUserRole={
                    user?.role ? roleLabelMapping[user.role] : ''
                  }
                  canRecordOfferEvents={canRecordOfferEvents}
                  onRecordOfferEvent={handleRecordOfferEvent}
                />
              </div>
            </div>
          </div>
        </>
      )}
      <AddFeedbackModal
        isOpen={isAddFeedbackModalOpen}
        isLoading={isLoading}
        onClose={handleAddFeedbackModalClose}
        onSubmit={onAddFeedback}
      />
      <ViewFeedbackModal
        isOpen={isViewFeedbackModalOpen}
        isLoading={isLoading}
        feedback={candidateFeedback}
        onClose={handleViewFeedbackModalClose}
        onGetFeedback={onGetFeedback}
      />
      <DebriefModal
        isOpen={debriefFor !== null}
        isLoading={isLoading}
        decision={debriefFor ?? undefined}
        candidateName={displayName}
        onClose={handleDebriefClose}
        onSubmit={handleDebriefSubmit}
      />
      <MaskedResumeModal
        isOpen={isMaskedResumeOpen}
        name={displayName}
        onClose={() => setIsMaskedResumeOpen(false)}
      />
      <ShareProfileModal
        isOpen={isShareProfileModalOpen}
        isLoading={isLoading}
        name={displayName}
        hiringManagers={hiringManagers}
        onClose={handleShareProfileModalClose}
        onFetchHiringManagers={onFetchHiringManagers}
        onShareCandidate={onShareCandidate}
      />
      <InterviewTranscriptModal
        isOpen={isInterviewTranscriptModalOpen}
        isLoading={isLoading}
        name={displayName}
        candidateInterview={candidateInterview}
        onClose={handleInterviewTranscriptModalClose}
      />
    </Layout>
  );
};

export default memo(Candidate);
